import { useState, useEffect, useRef } from "react";
import { FormattedMessage as FM } from "react-intl";
import { signal } from "@preact/signals-react";
import proxy from "api/proxy";

import Dialog from "componentsv2/modal/Dialog";
import Dropdown from "componentsv2/form/Dropdown";
import { PlusIcon } from "components/Icons";
import { classNames as cn } from "tools/ui";
import { getStorage } from "store/local";
import { isEventWithinLimit } from "./helper";
import CardEvents from "./components/CardEvents";

function toTrytonTime(value) {
  if (!value) return null;
  const parts = String(value).split(":");
  if (parts.length === 2) return `${value}:00`;
  return value;
}

export function toTrytonDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const match = String(value).match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : String(value);
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const parts = String(timeStr).split(":");
  if (parts.length < 2) return "";
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
}

function toDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

async function fetchCategories() {
  const domain = [["absenteeism", "=", true]];
  try {
    const { data } = await proxy.search("staff.event_category", domain, [
      "id",
      "rec_name",
    ]);
    let rangeMap = new Map();
    try {
      const { data: withRange } = await proxy.search(
        "staff.event_category",
        domain,
        ["id", "range_type"],
        100
      );
      rangeMap = new Map(
        (withRange || []).map((r) => [r.id, r.range_type || ""]),
      );
    } catch (error) {
      console.error("Error fetching range_type:", error);
    }
    return (data || []).map((r) => ({
      id: r.id,
      value: r.rec_name ?? r.name ?? "",
      name: r.rec_name ?? r.name ?? "",
      range_type: rangeMap.get(r.id) || "",
    }));
  } catch (error) {
    console.error("Error fetching event categories:", error);
    return [];
  }
}

async function fetchEvents(employee, eventDate) {
  try {
    const { data, error } = await proxy.methodCall({
      model: "staff.shift",
      method: "get_events_by_employee",
      args: [[employee], eventDate, eventDate],
    });
    if (error) throw new Error(error);
    return data?.[String(employee)] || [];
  } catch (error) {
    console.error("Error fetching events:", error);
    return [];
  }
}

function getId(value) {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (Array.isArray(value)) return value[0] ?? null;
  if (value instanceof Map) {
    const first = value.values().next().value;
    return typeof first === "number" ? first : (first?.id ?? null);
  }
  return value.id ?? null;
}

function getAccessDate(record) {
  const attendance = record?.attendance;
  const map =
    attendance instanceof Map
      ? attendance
      : attendance?.value instanceof Map
        ? attendance.value
        : null;
  if (!map || map.size === 0) return null;
  const first = map.values().next().value;
  const ts = first?.enter_timestamp;
  if (!ts) return null;
  if (ts instanceof Date) {
    const y = ts.getUTCFullYear();
    const m = String(ts.getUTCMonth() + 1).padStart(2, "0");
    const d = String(ts.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return toTrytonDate(ts);
}

function findEntry(selected, permit) {
  return selected.find(
    (item) => item === permit || (item && item.value === permit),
  );
}

function isActive(selected, permit) {
  return findEntry(selected, permit) !== undefined;
}

function PermissionsChecklist(props) {
  const { label, readOnly: propReadOnly, style, record } = props;
  const employee = getId(record?.employee);
  const shiftDate = toTrytonDate(record?.shift_date);
  const eventDate = getAccessDate(record) || shiftDate;

  const staffConfig = getStorage("staffConfig");
  const eventShiftLimit = staffConfig?.event_shift_limit ?? 0;
  const isWithinLimit = isEventWithinLimit(eventDate || shiftDate, eventShiftLimit);
  const readOnly = propReadOnly || !isWithinLimit;
  const [selected, setSelected] = useState([]);
  const [openAddModal] = useState(() => signal(false));
  const [categoryData] = useState(() => signal(null));
  const [pending, setPending] = useState([]);
  const [dupWarn, setDupWarn] = useState(null);
  const savingRef = useRef(false);

  useEffect(() => {
    async function loadData() {
      const fechaInfo = {
        employee,
        shiftDate,
        accessDate: getAccessDate(record),
        eventDate,
        attendance: record?.attendance,
      };
      if (!employee || !eventDate) {
        setSelected([]);
        return;
      }
      const [cats, events] = await Promise.all([
        fetchCategories(),
        fetchEvents(employee, eventDate),
      ]);
      const catsByName = new Map(cats.map((c) => [c.value, c]));
      const loaded = (events || []).map((ev) => {
        const cat = catsByName.get(ev.category_name);
        return {
          id: ev.id,
          value: cat?.value ?? ev.category_name ?? ev.description ?? "",
          category: cat?.id ?? null,
          range_type: cat?.range_type || (ev.start_time ? "times" : "dates"),
          start: formatTime(ev.start_time),
          end: formatTime(ev.end_time),
          start_date: toDate(ev.start_date),
          end_date: toDate(ev.end_date),
        };
      });
      setSelected(loaded);
    }
    loadData();
  }, [props.reloadId, employee, eventDate]);

  function applySelected(next) {
    setSelected(next);
  }

  async function createEvent(permitMeta, rangeValues) {
    const storeRec = {
      employee,
      category: permitMeta.id,
      event_date: eventDate,
      description: permitMeta.name,
      reference: "Turno programado",
      quantity: 1,
    };
    if (rangeValues) {
      if (permitMeta.range_type === "times") {
        storeRec.start_time = toTrytonTime(rangeValues.start);
        storeRec.end_time = toTrytonTime(rangeValues.end);
      } else if (permitMeta.range_type === "dates") {
        storeRec.start_date = rangeValues.start_date || eventDate;
        storeRec.end_date = rangeValues.end_date || eventDate;
      }
    }
    const { data, error } = await proxy.create("staff.event", storeRec, ["id"]);
    if (error) throw new Error(error);
    return data?.[0] ?? { id: null };
  }

  async function removeEntry(entry) {
    if (readOnly || savingRef.current || !entry) return;
    if (entry.id) {
      savingRef.current = true;
      await proxy.remove({ model: "staff.event", ids: [entry.id] });
      savingRef.current = false;
    }
    applySelected(selected.filter((item) => item !== entry));
  }

  function openAddNovedad() {
    if (readOnly) return;
    categoryData.value = null;
    setPending([]);
    setDupWarn(null);
    openAddModal.value = true;
  }

  function onCategoryChange(name, value) {
    if (!value) return;
    const catName = value.rec_name ?? value.name ?? "";
    if (
      isActive(selected, catName) ||
      pending.some((p) => p.value === catName)
    ) {
      setDupWarn(catName);
      categoryData.value = null;
      return;
    }
    setDupWarn(null);
    const rangeType = value.range_type || "";
    setPending([
      ...pending,
      {
        id: value.id,
        value: catName,
        range_type: rangeType,
        start: "",
        end: "",
        start_date: eventDate || "",
        end_date: eventDate || "",
      },
    ]);
    categoryData.value = null;
  }

  function onPendingChange(item, field, value) {
    setPending(pending.map((p) => (p === item ? { ...p, [field]: value } : p)));
  }

  function onRemovePending(item) {
    setPending(pending.filter((p) => p !== item));
  }

  async function onAddEvent() {
    if (readOnly || pending.length === 0) return;
    if (!employee || !eventDate) {
      console.error("Missing employee or shift_date to create event");
      return;
    }
    const created = [];
    try {
      savingRef.current = true;
      for (const item of pending) {
        const permitMeta = {
          id: item.id,
          name: item.value,
          range_type: item.range_type,
        };
        let rangeValues = null;
        if (permitMeta.range_type === "times") {
          rangeValues = { start: item.start, end: item.end };
        } else if (permitMeta.range_type === "dates") {
          rangeValues = {
            start_date: item.start_date,
            end_date: item.end_date,
          };
        }
        const newEvent = await createEvent(permitMeta, rangeValues);
        created.push({
          id: newEvent.id,
          value: item.value,
          category: item.id,
          range_type: item.range_type,
          start: item.start,
          end: item.end,
          start_date: item.start_date || eventDate,
          end_date: item.end_date || eventDate,
        });
      }
      applySelected([...selected, ...created]);
      setPending([]);
      setDupWarn(null);
      openAddModal.value = false;
    } catch (error) {
      console.error("Error creating events:", error);
    } finally {
      savingRef.current = false;
    }
  }

  const showHeader = !readOnly || selected.length > 0;

  return (
    <div className={cn(style, "flex flex-col w-full")}>
      {showHeader && (
        <span className="flex items-center mt-2 mb-3 ml-1 max-h-5 text-sm text-neutral-500">
          {label && <FM id={label} />}
          {!readOnly && (
            <button
              type="button"
              disabled={readOnly}
              onClick={openAddNovedad}
              title="Agregar novedad"
              className="ml-2  flex items-center justify-center size-6 rounded-full border border-cyan-600 bg-cyan-600 text-white hover:bg-cyan-800 transition active:scale-95"
            >
              <PlusIcon width={12} height={12} className="fill-current size-5" />
            </button>
          )}
        </span>
      )}

      {selected.length > 0 && (
        <CardEvents
          entries={selected}
          readOnly={readOnly}
          onRemove={removeEntry}
        />
      )}

      <Dialog
        openModal={openAddModal}
        size="w-screen md:w-1/2"
        title="Agrega una novedad"
      >
        <div className="flex flex-col gap-4 min-h-[40vh]">
          {openAddModal.value && (
            <Dropdown
              label="staff.shift.event_category"
              name="event_category"
              model="staff.event_category"
              preloaded
              attrs={["range_type"]}
              limit={100}
              recSearch={() => [["absenteeism", "=", true]]}
              data={categoryData}
              onChange={onCategoryChange}
              placeholder="Selecciona una novedad"
            />
          )}
          {pending.length > 0 && (
            <div className="flex flex-col gap-3">
              {pending.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 border border-sky-200 bg-white rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-medium text-neutral-700">
                      <span className="w-2 h-2 rounded-full bg-sky-500 shrink-0" />
                      {item.value}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemovePending(item)}
                      title="Quitar de la lista"
                      className="flex items-center justify-center w-6 h-6 rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100 hover:text-rose-700 transition active:scale-95 shrink-0"
                    >
                      <span className="text-xs leading-none">✕</span>
                    </button>
                  </div>
                  {item.range_type === "times" && (
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-2 text-xs text-neutral-600">
                        <span>Hora inicio</span>
                        <input
                          type="time"
                          value={item.start}
                          onChange={(e) =>
                            onPendingChange(item, "start", e.target.value)
                          }
                          className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-sky-400"
                        />
                      </label>
                      <label className="flex items-center gap-2 text-xs text-neutral-600">
                        <span>Hora fin</span>
                        <input
                          type="time"
                          value={item.end}
                          onChange={(e) =>
                            onPendingChange(item, "end", e.target.value)
                          }
                          className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-sky-400"
                        />
                      </label>
                    </div>
                  )}
                  {item.range_type === "dates" && (
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-2 text-xs text-neutral-600">
                        <span>Fecha inicio</span>
                        <input
                          type="date"
                          value={item.start_date}
                          onChange={(e) =>
                            onPendingChange(item, "start_date", e.target.value)
                          }
                          className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-sky-400"
                        />
                      </label>
                      <label className="flex items-center gap-2 text-xs text-neutral-600">
                        <span>Fecha fin</span>
                        <input
                          type="date"
                          value={item.end_date}
                          onChange={(e) =>
                            onPendingChange(item, "end_date", e.target.value)
                          }
                          className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-sky-400"
                        />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {dupWarn && (
            <p className="text-xs text-orange-600">
              Esta novedad ya está agregada: {dupWarn}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2 absolute bottom-4 left-4 right-4">
            <button
              type="button"
              onClick={() => {
                openAddModal.value = false;
              }}
              className="px-4 py-2 rounded-lg text-sm bg-white text-neutral-600 border border-slate-300 hover:bg-neutral-50 transition active:scale-95"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onAddEvent}
              disabled={pending.length === 0}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-cyan-600 text-white border border-cyan-600 hover:bg-cyan-700 transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Agregar ({pending.length})
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default PermissionsChecklist;
